import { ColumnDef } from '@tanstack/react-table';
import { format, formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { EventNameDisplay } from 'src/components/Events/EventInfo';
import { isCampaignActive } from 'src/components/SponsoredListings';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { CampaignSalesAggregate } from 'src/contexts/AdCampaignsDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption_Standard } from 'src/modals/EditTableColumns/EditTableColumns.constants';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronUpIconV2 } from 'src/svgs/Viagogo/goicons/ChevronUpIconV2';
import * as styles from 'src/tables/SponsoredListings/SponsoredListingsTable.css';
import { TableShimmeringDiv } from 'src/tables/Table';
import { MOCK_SPONSORED_LISTINGS_DATA } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  applyNumberFormatting,
  formatPercent,
} from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  BiddingMode,
  Feature,
  FlightDuration,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
  TicketClassInfo,
  UiCurrency,
} from 'src/WebApiController';

import { BaseBidInput } from '../BaseBidInput';

export type CampaignsTableRow = {
  id: string;
  campaignId: number;
  name: string;
  eventId?: number;
  eventName?: string;
  eventNameParts?: string[];
  eventDate?: Date;
  currency?: UiCurrency;
  salesData?: CampaignSalesAggregate;
  hasChildren: boolean;
  biddingMode: BiddingMode;
  flightDuration: FlightDuration;
  campaign?: SponsoredListingsCampaign;
  flight?: SponsoredListingsFlight;
  flights?: SponsoredListingsFlight[];
};

export const getTableColumnDefinitions = (
  onStartWork: () => void,
  onStopWork: (success: boolean) => void,
  disabled?: boolean
): ColumnDef<CampaignsTableRow>[] => [
  {
    id: 'eventName',
    size: 250,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Event} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventName,
    cell: function Cell({ getValue, row: { original } }) {
      const eventName = getValue<string | undefined>();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (!original.eventNameParts) {
        return null;
      }

      return (
        <BaseCell align="left">
          <Button
            variant="text"
            title={eventName}
            onClick={(e) => {
              if (original.eventId) {
                window.open(
                  createInventoryEventUrl(original.eventId.toString(), true),
                  '_blank',
                  'noopener noreferrer'
                );
              }
            }}
          >
            <EventNameDisplay
              eventName={original.eventNameParts[0]}
              eventNameConnector={original.eventNameParts[1]}
              eventSubName={original.eventNameParts[2]}
            />
          </Button>
        </BaseCell>
      );
    },
  },
  {
    id: 'eventDate',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.EventDate} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventDate,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<Date | undefined>();
      if (!value) {
        return null;
      }

      const eventDate = format(value, 'MMM d - h:mm a');

      return (
        <BaseCell align="left" title={value.toString()}>
          {eventDate}
        </BaseCell>
      );
    },
  },
  {
    id: 'campaign',
    size: 250,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.CampaignName} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.name,
    sortingFn: 'textCaseSensitive',
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string>();
      const isActive = original.flight?.isCurrent;
      return (
        <BaseCell align="left">
          <Stack gap="m" direction="row" alignItems="center">
            <span>{original.campaign ? <b>{value}</b> : value}</span>
            {isActive && (
              <div className={styles.activeBadge}>
                <Content id={ContentId.Active} />
              </div>
            )}
          </Stack>
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Content id={ContentId.CampaignsTotal} />
        </BaseCell>
      );
    },
  },
  {
    id: 'startDate',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.StartDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      (data.campaign?.dateRange ?? data.flight?.dateRange)?.start,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }
      const value = getValue<string | undefined>();
      if (!value) {
        return null;
      }

      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'endDate',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.EndDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      (data.campaign?.dateRange ?? data.flight?.dateRange)?.end,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }
      const value = getValue<string | undefined>();
      if (!value) {
        return null;
      }

      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'revenue',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Revenue} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      const salesData = data.salesData;
      let revenue = salesData?.totalNetProceeds;

      if (data.flights && salesData) {
        // For aggregated flights, sum up revenue from all flights
        revenue = data.flights.reduce((sum, flight) => {
          // Use metrics-based salesProceeds if available, otherwise fall back to salesData
          const metricsRevenue = flight.salesProceeds;
          if (metricsRevenue != null) {
            return sum + metricsRevenue;
          }
          const flightData = salesData.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
      } else if (data.flight && salesData) {
        // If this is a flight row, use metrics-based salesProceeds if available
        revenue =
          data.flight.salesProceeds != null
            ? data.flight.salesProceeds
            : salesData.flightsAggregate?.[data.flight.flightId]
                ?.totalNetProceeds;
      } else if (data.campaign) {
        // For campaign-level row, use metrics-based salesProceeds if available
        revenue =
          data.campaign.salesProceeds != null
            ? data.campaign.salesProceeds
            : salesData?.totalNetProceeds;
      }

      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $100 and $10,000
        revenue = Math.floor(Math.random() * 9900) + 100;
      }

      return revenue;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const revenue = getValue<number>();

      return (
        <BaseCell align="right" style={{ padding: vars.spacing.xs }}>
          {!!revenue && (
            <div
              className={revenue > 0 ? styles.revenuePositiveCell : undefined}
            >
              {applyNumberFormatting({
                inputNumber: revenue,
                currencyCode: original.currency?.code,
                currencyDecimalDigits: original.currency?.dec,
              })}
            </div>
          )}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.salesData?.totalNetProceeds &&
            applyNumberFormatting({
              inputNumber: original.salesData.totalNetProceeds,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'spend',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.AdSpend} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up spend from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      }
      return data.campaign?.totalSpend ?? data.flight?.flightSpend;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const spend = getValue<number | undefined>();

      return (
        <BaseCell align="right">
          {!!spend &&
            applyNumberFormatting({
              inputNumber: spend,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.campaign?.totalSpend &&
            applyNumberFormatting({
              inputNumber: original.campaign.totalSpend,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'roas',
    size: 100,
    header: () => <TableHeaderCellDiv align="right">ROAS</TableHeaderCellDiv>,
    accessorFn: (data) => {
      const salesData = data.salesData;

      let revenue = salesData?.totalNetProceeds ?? 0;
      let spend = data.campaign?.totalSpend;

      if (data.flights && salesData) {
        // For aggregated flights, sum up revenue and spend from all flights
        revenue = data.flights.reduce((sum, flight) => {
          // Use metrics-based salesProceeds if available, otherwise fall back to salesData
          const metricsRevenue = flight.salesProceeds;
          if (metricsRevenue != null) {
            return sum + metricsRevenue;
          }
          const flightData = salesData?.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
        spend = data.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      } else if (data.flight && salesData) {
        // If this is a flight row, use metrics-based salesProceeds if available
        revenue =
          data.flight.salesProceeds != null
            ? data.flight.salesProceeds
            : salesData.flightsAggregate?.[data.flight.flightId]
                ?.totalNetProceeds;
        spend = data.flight?.flightSpend;
      } else if (data.campaign) {
        // For campaign-level row, use metrics-based salesProceeds if available
        revenue =
          data.campaign.salesProceeds != null
            ? data.campaign.salesProceeds
            : (salesData?.totalNetProceeds ?? 0);
        spend = data.campaign.totalSpend;
      }

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $1 and budget * 10
        revenue =
          Math.floor(
            Math.random() *
              ((data.campaign?.totalBudget ?? data?.flight?.flightBudget ?? 1) *
                10 -
                1)
          ) + 1;
      }

      const roas = spend ? revenue / spend : 0;

      return roas;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const roas = getValue<number>();
      return (
        <BaseCell align="right">{!!roas && formatPercent(roas, 2)}</BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      const totalRevenue = original.salesData?.totalNetProceeds ?? 0;
      const totalSpend = original.campaign?.totalSpend;

      const totalRoas = totalSpend ? totalRevenue / totalSpend : 0;

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>{!!totalRevenue && formatPercent(totalRoas, 2)}</span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'sales-count',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Sales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      const salesData = data.salesData;
      let saleCount = salesData?.saleCount;

      if (data.flights && salesData) {
        // For aggregated flights, sum up sale counts from all flights
        saleCount = data.flights.reduce((sum, flight) => {
          // Use metrics-based salesCount if available, otherwise fall back to salesData
          const metricsSalesCount = flight.salesCount;
          if (metricsSalesCount != null) {
            return sum + metricsSalesCount;
          }
          const flightData = salesData?.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.saleCount || 0);
        }, 0);
      } else if (data.flight && salesData) {
        // If this is a flight row, use metrics-based salesCount if available
        saleCount =
          data.flight.salesCount != null
            ? data.flight.salesCount
            : salesData.flightsAggregate?.[data.flight.flightId]?.saleCount;
      } else if (data.campaign) {
        // For campaign-level row, use metrics-based salesCount if available
        saleCount =
          data.campaign.salesCount != null
            ? data.campaign.salesCount
            : salesData?.saleCount;
      }

      return saleCount;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const saleCount = getValue<number>();

      return (
        <BaseCell align="right">
          {saleCount != null &&
            applyNumberFormatting({
              inputNumber: saleCount,
              precision: FormatOption_Standard.Whole,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.salesData?.saleCount == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.salesData.saleCount,
            precision: FormatOption_Standard.Whole,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'historic-potential-impressions',
    size: 120,
    header: () => (
      <TableHeaderCellDiv
        align="right"
        customTooltipContent={
          <Content id={ContentId.HistoricPotentialImpressions} />
        }
      >
        <Content id={ContentId.HistoricPotentialImpressionsAbbrev} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up from all flights
        return data.flights.reduce((sum, flight) => {
          return sum + (flight.historicPotentialImpressions || 0);
        }, 0);
      } else if (data.flight) {
        return data.flight.historicPotentialImpressions;
      }
      return data.campaign?.historicPotentialImpressions;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<number>();

      return (
        <BaseCell align="right">
          {value != null &&
            applyNumberFormatting({
              inputNumber: value,
              precision: FormatOption_Standard.Whole,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.historicPotentialImpressions == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.campaign.historicPotentialImpressions,
            precision: FormatOption_Standard.Whole,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'actual-impressions',
    size: 120,
    header: () => (
      <TableHeaderCellDiv
        align="right"
        customTooltipContent={<Content id={ContentId.ActualImpressions} />}
      >
        <Content id={ContentId.ActualImpressionsAbbrev} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up from all flights
        return data.flights.reduce((sum, flight) => {
          return sum + (flight.actualImpressions || 0);
        }, 0);
      } else if (data.flight) {
        return data.flight.actualImpressions;
      }
      return data.campaign?.actualImpressions;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<number>();

      return (
        <BaseCell align="right">
          {value != null &&
            applyNumberFormatting({
              inputNumber: value,
              precision: FormatOption_Standard.Whole,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.actualImpressions == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.campaign.actualImpressions,
            precision: FormatOption_Standard.Whole,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'impression-share',
    size: 100,
    header: () => (
      <TableHeaderCellDiv
        align="right"
        customTooltipContent={<Content id={ContentId.ImpressionShare} />}
      >
        <Content id={ContentId.ImpressionShareAbbrev} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, calculate weighted average
        const totalActual = data.flights.reduce((sum, flight) => {
          return sum + (flight.actualImpressions || 0);
        }, 0);
        const totalPotential = data.flights.reduce((sum, flight) => {
          return sum + (flight.historicPotentialImpressions || 0);
        }, 0);
        return totalPotential > 0 ? totalActual / totalPotential : 0;
      } else if (data.flight) {
        return data.flight.impressionShare;
      }
      return data.campaign?.impressionShare;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<number>();

      return (
        <BaseCell align="right">
          {value != null && formatPercent(value, 1)}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.impressionShare == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {formatPercent(original.campaign.impressionShare, 1)}
        </BaseCell>
      );
    },
  },
  {
    id: 'clicks',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Clicks} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up from all flights
        return data.flights.reduce((sum, flight) => {
          return sum + (flight.clicks || 0);
        }, 0);
      } else if (data.flight) {
        return data.flight.clicks;
      }
      return data.campaign?.clicks;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<number>();

      return (
        <BaseCell align="right">
          {value != null &&
            applyNumberFormatting({
              inputNumber: value,
              precision: FormatOption_Standard.Whole,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const hasFeature = useUserHasFeature(
        Feature.SponsoredListingsV2_ReportingMetrics
      );

      if (!hasFeature) {
        return null;
      }

      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.clicks == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.campaign.clicks,
            precision: FormatOption_Standard.Whole,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'potential-sales',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.PotentialSales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      let potentialSales = 0;

      if (data.flights) {
        potentialSales = data.flights.reduce(
          (sum, flight) => sum + (flight.potentialSales ?? 0),
          0
        );
      } else if (data.flight) {
        potentialSales = data.flight.potentialSales ?? 0;
      } else if (data.campaign) {
        potentialSales = data.campaign.potentialSales ?? 0;
      }

      return potentialSales;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const potentialSales = getValue<number>();

      return (
        <BaseCell align="right">
          {potentialSales != null &&
            applyNumberFormatting({
              inputNumber: potentialSales,
              precision: FormatOption_Standard.Whole,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.potentialSales == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.campaign.potentialSales,
            precision: FormatOption_Standard.Whole,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'potential-revenue',
    size: 100,
    header: () => (
      <TableHeaderCellDiv
        align="right"
        customTooltipContent={<Content id={ContentId.PotentialRevenue} />}
      >
        <Content id={ContentId.PotentialRevenueAbbrev} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      let potentialRevenue = 0;

      if (data.flights) {
        potentialRevenue = data.flights.reduce(
          (sum, flight) => sum + (flight.potentialRevenue ?? 0),
          0
        );
      } else if (data.flight) {
        potentialRevenue = data.flight.potentialRevenue ?? 0;
      } else if (data.campaign) {
        potentialRevenue = data.campaign.potentialRevenue ?? 0;
      }

      return potentialRevenue;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const potentialRevenue = getValue<number>();

      return (
        <BaseCell align="right">
          {potentialRevenue != null &&
            applyNumberFormatting({
              inputNumber: potentialRevenue,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (original.campaign?.potentialRevenue == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {applyNumberFormatting({
            inputNumber: original.campaign.potentialRevenue,
            currencyCode: original.currency?.code,
            currencyDecimalDigits: original.currency?.dec,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'cpc',
    size: 150,
    header: () => <TableHeaderCellDiv align="right">CPC</TableHeaderCellDiv>,
    accessorFn: (data) => {
      if (data.flights) {
        return null;
      }
      return data.campaign?.baseBid ?? data.flight?.baseBid;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const cpc = getValue<number | null>();

      return (
        <BaseCell align="right">
          {original.biddingMode === BiddingMode.Manual ? (
            original.campaign ? (
              <BaseBidInput currencyCode={original.currency?.code ?? 'USD'} />
            ) : (
              applyNumberFormatting({
                inputNumber: cpc,
                currencyCode: original.currency?.code,
                currencyDecimalDigits: original.currency?.dec,
              })
            )
          ) : null}
        </BaseCell>
      );
    },
  },
  {
    id: 'budget',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Budget} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up budget from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flightBudget || 0),
          0
        );
      }
      return data.campaign?.totalBudget ?? data.flight?.flightBudget;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const budget = getValue() as number | null;

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: budget,
            currencyCode: original.currency?.code,
            currencyDecimalDigits: original.currency?.dec,
          })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.campaign?.totalBudget &&
            applyNumberFormatting({
              inputNumber: original.campaign.totalBudget,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'budget-remaining',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Remaining} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      let budget: number;
      let spend: number;
      if (data.flights) {
        // For aggregated flights, sum up budget and spend from all flights
        budget = data.flights.reduce(
          (sum, flight) => sum + (flight.flightBudget || 0),
          0
        );
        spend = data.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      } else {
        // Calculate budget left for individual campaign or flight
        budget = data.campaign?.totalBudget ?? data.flight?.flightBudget ?? 0;
        spend = data.campaign?.totalSpend ?? data.flight?.flightSpend ?? 0;
      }

      const remaining = budget - spend;

      return remaining;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const remaining = getValue<number>();

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: remaining,
            currencyCode: original.currency?.code,
            currencyDecimalDigits: original.currency?.dec,
          })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {(!!original.campaign?.totalBudget ||
            !!original.campaign?.totalSpend) &&
            applyNumberFormatting({
              inputNumber:
                (original.campaign.totalBudget ?? 0) -
                (original.campaign.totalSpend ?? 0),
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'target',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Target} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaign?.targetTicketClassIds,
    enableSorting: false,
    cell: function Cell({ getValue, row: { original } }) {
      const { venueMapInfo } = useEventMapContext();
      const allZoneTxt = useContent(ContentId.AllZones);

      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      const ticketClassesById = useMemo(() => {
        if (!venueMapInfo) return {};

        return venueMapInfo.sections
          .flatMap((s) => s.rows.map((r) => r.tktClass))
          .reduce(
            (record, ticketClass) => {
              if (ticketClass?.id) {
                record[ticketClass.id] = ticketClass;
              }
              return record;
            },
            {} as Record<number, TicketClassInfo>
          );
      }, [venueMapInfo]);

      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }

      const tktClassIds = getValue<number[] | undefined>();

      // Display target information
      let target = allZoneTxt;
      if (tktClassIds?.length && Object.keys(ticketClassesById).length > 0) {
        const targetedClasses = tktClassIds
          .map((id) => ticketClassesById[id]?.name)
          .filter(Boolean);

        if (targetedClasses.length > 0) {
          target = targetedClasses.join(', ');
        }
      }

      return <BaseCell align="left">{target}</BaseCell>;
    },
  },
  {
    id: 'actions',
    size: 100,
    header: () => <TableHeaderCellDiv align="center"></TableHeaderCellDiv>,
    accessorFn: (data) => data.campaign,
    cell: function Cell({
      getValue,
      row: { getCanExpand, getIsExpanded, toggleExpanded, original },
    }) {
      const campaign = getValue<SponsoredListingsCampaign | undefined>();

      const canExpand = getCanExpand();
      if (!canExpand) {
        return null;
      }
      const isExpanded = getIsExpanded();
      const Icon = isExpanded ? ChevronUpIconV2 : ChevronDownIconV2;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      return (
        <BaseCell
          align="center"
          style={{ padding: vars.spacing.sm, paddingRight: vars.spacing.m }}
        >
          <Stack gap="m" alignItems="center" justifyContent="center">
            {campaign && (
              <>
                <UpdateCampaignStateButton
                  iconOnly
                  campaign={campaign}
                  state={
                    isCampaignActive(campaign)
                      ? AdCampaignState.Paused
                      : AdCampaignState.Active
                  }
                  onBeforePause={onStartWork}
                  onAfterPause={onStopWork}
                  disabled={disabled}
                />
                <BoostListingButton
                  campaign={campaign}
                  iconOnly
                  disabled={disabled}
                />
              </>
            )}
            <Icon
              size={vars.iconSize.s}
              withHoverEffect
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleExpanded(!isExpanded);
              }}
            />
          </Stack>
        </BaseCell>
      );
    },
  },
];
